import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import en from '@angular/common/locales/en';
import nlBe from '@angular/common/locales/nl-BE';
import {
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { PRODUCTION_BOOTSTRAP } from './app/constants';
import { AccountService } from './app/shared/services/account.service';
import { ProductionService } from './app/shared/services/production.service';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { httpErrorInterceptor } from './app/http-error.interceptor';
import { ignoreAbortedInterceptor } from './app/ignore-aborted.interceptor';
import {
  extractSlugFromUrl,
  tenantHeaderInterceptor,
} from './app/shared/interceptors/tenant-header.interceptor';

registerLocaleData(nlBe);
registerLocaleData(en);

if (environment.production) {
  Sentry.init({
    dsn: 'https://b6f8716a871e48729864f9e6c104a252@o4504496418193408.ingest.sentry.io/4504496421404672',
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: ['localhost', /^\/api/, 'https://reservatie.app'],
    environment: 'production',
    tracesSampleRate: 1.0,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideAnimations(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        ignoreAbortedInterceptor,
        tenantHeaderInterceptor,
        httpErrorInterceptor,
      ])
    ),
    importProvidersFrom(
      AuthModule.forRoot({
        domain: environment.auth.domain,
        clientId: environment.auth.clientId,
        cacheLocation: 'localstorage',
        authorizationParams: {
          audience: environment.auth.audience,
          redirect_uri: window.location.origin,
          connection: 'email',
        },
        httpInterceptor: {
          allowedList: [
            {
              uriMatcher: (uri) => {
                if (!uri.startsWith('/api')) {
                  return false;
                }

                if (
                  uri.includes('bootstrap') ||
                  uri.startsWith('/api/account/current')
                ) {
                  return false;
                }

                return true;
              },
              allowAnonymous: true,
            },
          ],
        },
      })
    ),
    provideMatomo(
      {
        trackerUrl: 'https://matomo.samyn.co',
        siteId: '2',
        acceptDoNotTrack: true,
        runOutsideAngularZone: true,
      },
      withRouter()
    ),
    {
      provide: LOCALE_ID,
      useValue: 'nl-BE',
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: PRODUCTION_BOOTSTRAP,
      useFactory: (
        productionService: ProductionService,
        accountService: AccountService
      ) => {
        // Only bootstrap tenant data when there IS a tenant context:
        // a subdomain host or a /:slug path. Marketing pages have neither —
        // the call would just 404.
        const host = document.location.host.split(':')[0];
        const parts = host.split('.');
        const hasSubdomain =
          (parts.length === 2 && parts[1] === 'localhost') ||
          (parts.length === 3 && parts[0] !== 'www');
        const slug = extractSlugFromUrl(document.location.pathname);
        if (hasSubdomain || slug) {
          productionService.loadBootstrap().subscribe();
          accountService.refresh().subscribe();
        }
      },
      deps: [ProductionService, AccountService],
    },
    provideCharts(withDefaultRegisterables()),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
}).catch((err) => console.error(err));
